import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  Grid,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import InventoryIcon from '@mui/icons-material/Inventory';
import BranchList from './components/BranchList';
import ReservationList from './components/ReservationList';
import ReservationApplications from './components/ReservationApplications';
import EntryExitLog from './components/EntryExitLog';
import { getReservations, type ReservationListItem } from '../../services/reservationsApi';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../accounting/shared/StatsCard';
import { computeReservationDashboardStats } from './utils/reservationFilters';
import { useTranslation } from 'react-i18next';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reservations-tabpanel-${index}`}
      aria-labelledby={`reservations-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `reservations-tab-${index}`,
    'aria-controls': `reservations-tabpanel-${index}`,
  };
};

const Reservations: React.FC = () => {
  const { t, i18n } = useTranslation('other');
  const currentLocale = i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  const [tabValue, setTabValue] = useState(0);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [rows, setRows] = useState<ReservationListItem[]>([]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const loadDashboard = async () => {
    setIsLoadingStats(true);
    try {
      const response = await getReservations({
        type: 'ticket',
        page: 1,
        pageSize: 500,
      });
      setRows(response.rows);
    } catch (err) {
      console.error('Failed to load reservation dashboard data:', err);
      setRows([]);
    } finally {
      setIsLoadingStats(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const stats = useMemo(() => computeReservationDashboardStats(rows), [rows]);

  return (
    <Box sx={{ 
      width: '100%', 
      height: 'calc(100vh - 80px)', // Ana konteyner yüksekliği (üst kısımda navbar için boşluk bırakıldı)
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      gap: 2,
    }}>
      <Box sx={{ px: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
            <InventoryIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
            <Typography variant="h5" component="h1" fontWeight="bold">
              {t('reservations.pageTitle')}
            </Typography>
          </Box>
        </Box>

        <StatsCardsContainer loading={isLoadingStats}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCardItem
              icon={<EventNoteIcon />}
              color={STATS_COLORS.green}
              title={stats.totalCount.toLocaleString(currentLocale)}
              subtitle={t('reservations.stats.totalReservations')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCardItem
              icon={<CheckCircleOutlineIcon />}
              color={STATS_COLORS.purple}
              title={stats.approvedCount.toLocaleString(currentLocale)}
              subtitle={t('reservations.stats.approvedReservations')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCardItem
              icon={<PendingActionsIcon />}
              color={STATS_COLORS.blue}
              title={stats.pendingCount.toLocaleString(currentLocale)}
              subtitle={t('reservations.stats.pendingApplications')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCardItem
              icon={<QrCode2Icon />}
              color={stats.consumedCount > 0 ? STATS_COLORS.red : STATS_COLORS.grey}
              title={stats.consumedCount.toLocaleString(currentLocale)}
              subtitle={t('reservations.stats.usedAtEntrance')}
            />
          </Grid>
        </StatsCardsContainer>
      </Box>

      <Paper sx={{ 
        borderRadius: '12px', 
        boxShadow: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.06)',
      }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label={t('reservations.tabsAriaLabel')}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
              },
            }}
          >
            <Tab label={t('reservations.tabs.reservationList')} {...a11yProps(0)} />
            <Tab label={t('reservations.tabs.reservationApplications')} {...a11yProps(1)} />
            <Tab label={t('reservations.tabs.entryExitLog')} {...a11yProps(2)} />
            <Tab label={t('reservations.tabs.branches')} {...a11yProps(3)} />
          </Tabs>
        </Box>
        
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto',
          height: 'calc(100% - 48px)',
          backgroundColor: '#f7f8fb',
        }}>
          <TabPanel value={tabValue} index={0}>
            <ReservationList />
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <ReservationApplications />
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <EntryExitLog />
          </TabPanel>
          
          <TabPanel value={tabValue} index={3}>
            <BranchList />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default Reservations; 